import { useEffect } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import SalaryCalculator from "@/components/SalaryCalculator";
import { POSTS } from "@/data/salaryPosts";

const SITE = "https://hireds.in";

export default function SalaryCalculatorPage() {
  // Per-page SEO
  useEffect(() => {
    const oldTitle = document.title;
    document.title = "Govt Job Salary Calculator 2026 — Real In-Hand Pay | hireds.in";
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", "Calculate real in-hand salary for SSC, RRB, IBPS, SBI, RBI, UPSC, KVS posts using 7th CPC + 53% DA + city HRA. Updated Mar 2026 with actual basic pay and allowances.");
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/tools/salary-calculator`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Tools", url: "/tools" },
        { name: "Salary Calculator", url: "/tools/salary-calculator" },
      ]} />
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Free Tool</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Govt Job Salary-in-Hand Calculator 2026
        </h1>
        <p className="text-base text-slate-700 mt-2 leading-relaxed">
          Pick your post + city → see exact basic, DA, HRA, TA, NPS deduction and final monthly in-hand. Built from the official 7th CPC pay matrix and the latest DA notification (53% as of January 2026).
        </p>
      </header>

      <SalaryCalculator />

      <section className="mt-10">
        <h2 className="text-2xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          How this calculator works
        </h2>
        <div className="text-sm text-slate-700 leading-relaxed space-y-3">
          <p>For every Indian central government post, the monthly salary is built from <strong>five components</strong>:</p>
          <ol className="list-decimal list-outside ml-5 space-y-1">
            <li><strong>Basic Pay</strong> from the 7th CPC Pay Matrix (Level 1 to Level 14). This is the anchor of every other component.</li>
            <li><strong>Dearness Allowance (DA)</strong>, currently <strong>53%</strong> of basic, revised twice a year on 1 Jan and 1 Jul based on the All-India CPI for Industrial Workers.</li>
            <li><strong>House Rent Allowance (HRA)</strong>, 27% / 18% / 9% of basic for X / Y / Z class cities respectively (revised post-DA reaching 50%).</li>
            <li><strong>Transport Allowance (TA)</strong>, fixed slabs ranging from Rs. 1,350 (Level 1-2, Z city) to Rs. 7,200 (Level 9+, X city), plus DA on TA.</li>
            <li><strong>Post-specific allowances</strong> — running allowance for railway loco staff, MSP for defense, special allowance for banking, field/risk allowance for paramilitary.</li>
          </ol>
          <p>From the gross, two deductions reduce your take-home:</p>
          <ul className="list-disc list-outside ml-5 space-y-1">
            <li><strong>NPS contribution</strong> — 10% of (basic + DA), mandatory under the National Pension System.</li>
            <li><strong>Professional Tax</strong> — Rs. 200/month average, varies by state (Maharashtra Rs. 200, Karnataka Rs. 200, West Bengal Rs. 150 etc.).</li>
          </ul>
          <p>Income tax is <strong>not</strong> deducted in this calculator because it depends on your tax regime choice (old vs new) and your individual deductions (80C, 80D, HRA exemption, etc.). For Level 6+ posts, expect 10-30% additional reduction after IT.</p>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-2xl font-extrabold text-slate-900 mb-3" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Posts covered ({POSTS.length})
        </h2>
        <ul className="grid sm:grid-cols-2 gap-2 text-sm">
          {POSTS.map((p) => (
            <li key={p.post_slug} className="px-3 py-2 rounded border border-slate-200 bg-slate-50 text-slate-700">
              <strong>{p.sector}</strong> · {p.post}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10 bg-orange-50 border border-orange-200 rounded-xl p-5 md:p-6">
        <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Want a post added that's not in the list?
        </h2>
        <p className="text-sm text-slate-700 leading-relaxed">
          Drop us a note at <a href="mailto:editor@hireds.in" className="text-orange-700 underline">editor@hireds.in</a> with the notification PDF link — we add it within 48 hours.
        </p>
      </section>
    </div>
  );
}
